import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';
import { DashboardPage, PillTabs } from '../../components/PremiumDesign';
import { getCompanyId, QUOTATION_TAB_IDS, QUOTATION_TABS } from '../utils';
import { QuotationsTemplatesTab } from '../components/QuotationsTemplatesTab';
import { CustomerQuotationsTab } from '../components/CustomerQuotationsTab';
import { useQuotationsStateSnapshot } from '../hooks';

export function QuotationsPage() {
  const companyId = getCompanyId();
  const { can } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const { saveSnapshot, readSnapshot } = useQuotationsStateSnapshot();
  const urlTab = searchParams.get('tab');

  const getInitialTab = () => {
    if (
      urlTab === QUOTATION_TAB_IDS.orders ||
      urlTab === QUOTATION_TAB_IDS.templates
    ) {
      return urlTab;
    }
    const snapshot = readSnapshot();
    if (snapshot?.activeTab === QUOTATION_TAB_IDS.orders) {
      return QUOTATION_TAB_IDS.orders;
    }
    return QUOTATION_TAB_IDS.templates;
  };

  const [activeTab, setActiveTabState] = useState(getInitialTab);

  useEffect(() => {
    if (
      urlTab === QUOTATION_TAB_IDS.orders ||
      urlTab === QUOTATION_TAB_IDS.templates
    ) {
      setActiveTabState(urlTab);
      saveSnapshot({ activeTab: urlTab });
    } else if (!urlTab) {
      // If URL has no ?tab= param, set it to the activeTab
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', activeTab);
          return next;
        },
        { replace: true },
      );
    }
  }, [urlTab, activeTab, saveSnapshot, setSearchParams]);

  const handleTabChange = (tab) => {
    setActiveTabState(tab);
    saveSnapshot({ activeTab: tab });
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        return next;
      },
      { replace: true },
    );
  };

  const canCreate = can(PERMISSIONS.QUOTATIONS.CREATE);
  const canEdit = can(PERMISSIONS.QUOTATIONS.UPDATE);
  const canDelete = can(PERMISSIONS.QUOTATIONS.DELETE);

  return (
    <DashboardPage className="q-page h-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col w-full mx-auto min-w-0 max-w-full flex-1 min-h-0 overflow-hidden">
        <div className="shrink-0">
          <PillTabs
            items={QUOTATION_TABS}
            value={activeTab}
            onChange={handleTabChange}
            className="mb-4"
          />
        </div>

        {activeTab === QUOTATION_TAB_IDS.templates && (
          <QuotationsTemplatesTab
            companyId={companyId}
            canCreate={canCreate}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        )}

        {activeTab === QUOTATION_TAB_IDS.orders && (
          <CustomerQuotationsTab
            companyId={companyId}
            canCreate={canCreate}
            canDelete={canDelete}
          />
        )}
      </div>
    </DashboardPage>
  );
}

export default QuotationsPage;
